import { openRouter } from "../services/openRouterClient";
import { extractTextFromPDF, extractTextFromDOCX } from "./resumeParser";

/**
 * Parse resume using AI (OpenRouter API)
 * @param {File} file - Resume file (PDF or DOCX)
 * @returns {Promise<Object>} - Parsed resume data
 */
export const parseResumeWithAI = async (file) => {
  // 1. Extract text from file
  let text = "";
  const fileType = file.type;

  if (fileType === "application/pdf") {
    text = await extractTextFromPDF(file);
  } else if (
    fileType ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    text = await extractTextFromDOCX(file);
  } else {
    throw new Error(
      "Unsupported file format. Please upload a PDF or DOCX file."
    );
  }

  // 2. Create AI prompt for structured extraction
  const prompt = `You are a resume parser. Extract the following information from this resume and return ONLY valid JSON, no other text or explanation.

Expected JSON format:
{
  "first_name": "string",
  "last_name": "string",
  "email": "string",
  "phone": "string",
  "about": "professional summary or objective (2-3 sentences)",
  "skills": ["skill1", "skill2"],
  "education": [
    {
      "institution": "school name",
      "degree": "degree name",
      "field_of_study": "field/major",
      "start_year": "YYYY",
      "end_year": "YYYY or Present"
    }
  ],
  "experience": [
    {
      "company": "company name",
      "title": "job title",
      "start_date": "MMM YYYY",
      "end_date": "MMM YYYY or Present",
      "description": "key responsibilities and achievements"
    }
  ]
}

Resume text:
${text}

Return only the JSON object, no additional text.`;

  // 3. Call OpenRouter API
  try {
    const response = await openRouter.chat([
      {
        role: "user",
        content: prompt,
      },
    ]);

    // 4. Parse AI response - handle potential markdown formatting
    let jsonText = response.trim();

    // Remove markdown code blocks if present
    if (jsonText.startsWith("```json")) {
      jsonText = jsonText.replace(/```json\n?/g, "").replace(/```\n?$/g, "");
    } else if (jsonText.startsWith("```")) {
      jsonText = jsonText.replace(/```\n?/g, "").replace(/```\n?$/g, "");
    }

    const parsedData = JSON.parse(jsonText);

    // 5. Transform to expected format
    return {
      first_name: parsedData.first_name || "",
      last_name: parsedData.last_name || "",
      email: parsedData.email || "",
      phone: parsedData.phone || "",
      about: parsedData.about || "",
      skills: parsedData.skills || [],
      extracted_text: text,
      education: (parsedData.education || []).map((edu) => ({
        institution: edu.institution || "",
        degree: edu.degree || "",
        field_of_study: edu.field_of_study || "",
        start_year: edu.start_year || "",
        end_year: edu.end_year || "",
        year: edu.end_year || "", // For backward compatibility
      })),
      experience: (parsedData.experience || []).map((exp) => ({
        company: exp.company || "",
        title: exp.title || "",
        start_date: exp.start_date || "",
        end_date: exp.end_date || "",
        dates: exp.end_date
          ? `${exp.start_date || ""} - ${exp.end_date}`
          : exp.start_date || "",
        description: exp.description || "",
      })),
    };
  } catch (error) {
    console.error("AI parsing failed:", error);
    throw new Error(
      `Failed to parse resume with AI: ${error.message || "Unknown error"}`
    );
  }
};

export default parseResumeWithAI;
